import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { publicApi } from '@/lib/api';
import { absoluteImageUrl } from '@/components/admin/ImageUploader';
import { FIELD_NOTE_CATEGORIES, formatDate, labelFor } from '@/lib/taxonomies';
import { useSeo } from '@/lib/seo';
import { FIELD_NOTES } from '@/constants/testIds';

export default function FieldNotesListPage() {
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('all');
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useSeo({
    title: 'Field Notes | A Guy Who Hunts',
    description:
      'Long-form writing on scouting, trail cameras, public land, small properties, hunts, lessons, and the craft of documenting a hunting season honestly.',
  });

  useEffect(() => {
    const controller = new AbortController();
    const t = setTimeout(() => {
      setLoading(true);
      publicApi
        .fieldNotes({ q: q || undefined, category: category !== 'all' ? category : undefined })
        .then(setNotes)
        .finally(() => setLoading(false));
    }, 250);
    return () => {
      clearTimeout(t);
      controller.abort();
    };
  }, [q, category]);

  const categoryOptions = useMemo(() => [{ value: 'all', label: 'All categories' }, ...FIELD_NOTE_CATEGORIES], []);

  return (
    <div>
      <section className="relative border-b border-[hsl(var(--border))] hero-radial">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <p className="kicker">Field notes</p>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-2 leading-tight">
            The writing part of the season.
          </h1>
          <p className="mt-4 max-w-2xl text-[hsl(var(--muted-foreground))]">
            Longer entries on scouting, trail cameras, small-property observations, hunts, and lessons learned the
            honest way.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pt-8">
        <div className="grid gap-3 sm:grid-cols-[1fr_auto] items-center">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search field notes"
              className="pl-9 h-11"
              data-testid={FIELD_NOTES.searchInput}
              aria-label="Search field notes"
            />
          </div>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="h-11 w-full sm:w-[220px]" data-testid={FIELD_NOTES.categorySelect}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {categoryOptions.map((c) => (
                <SelectItem key={c.value} value={c.value}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <p className="mt-3 text-xs text-[hsl(var(--muted-foreground))]">
          {loading ? 'Loading…' : `${notes.length} ${notes.length === 1 ? 'note' : 'notes'}`}
        </p>
      </section>

      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10 pb-20">
        {loading ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-72 rounded-2xl bg-[hsl(var(--muted))] animate-pulse" />
            ))}
          </div>
        ) : notes.length === 0 ? (
          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-10 text-center">
            <p className="font-serif text-2xl">No notes match yet.</p>
            <p className="mt-2 text-[hsl(var(--muted-foreground))]">Try a different search or category.</p>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {notes.map((n) => (
              <Link key={n.slug} to={`/field-notes/${n.slug}`} data-testid={FIELD_NOTES.card} className="group">
                <Card className="h-full overflow-hidden border-[hsl(var(--border))] card-elev card-elev-hover">
                  <AspectRatio ratio={4 / 3}>
                    <img
                      src={absoluteImageUrl(n.hero_image?.url)}
                      alt={n.hero_image?.alt || n.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                      loading="lazy"
                    />
                  </AspectRatio>
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
                      <Badge variant="secondary" className="font-normal">
                        {labelFor(FIELD_NOTE_CATEGORIES, n.category)}
                      </Badge>
                      <span>•</span>
                      <time>{formatDate(n.date)}</time>
                    </div>
                    <h3 className="font-serif text-xl leading-snug mt-2 tracking-tight">{n.title}</h3>
                    <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] line-clamp-3">{n.excerpt}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
